import { useTranslation } from 'react-i18next'
import { Checkbox, Input } from '@structural-climbing/ui'
import { isValidPhone } from '@structural-climbing/validation'

/** The adult signing for someone under 18, as typed on the form. */
export interface GuardianValue {
  firstName: string
  lastName: string
  /** Only asked where the minor has an address of their own: a member. */
  email: string
  /** Asked when a minor joins, so the gym can reach the parent. */
  phone: string
  declared: boolean
}

export const EMPTY_GUARDIAN: GuardianValue = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  declared: false,
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Whether the guardian's address is the minor's own, which proves nothing. */
export function guardianEmailIsOwn(value: GuardianValue, memberEmail?: string | null): boolean {
  return !!memberEmail && value.email.trim().toLowerCase() === memberEmail.trim().toLowerCase()
}

/** Whether there is enough to go on to signing. */
export function guardianComplete(
  value: GuardianValue,
  options: { needsEmail: boolean; memberEmail?: string | null; needsPhone?: boolean },
): boolean {
  if (!value.firstName.trim() || !value.lastName.trim() || !value.declared) return false
  if (options.needsPhone && !isValidPhone(value.phone)) return false
  if (!options.needsEmail) return true
  return EMAIL_RE.test(value.email.trim()) && !guardianEmailIsOwn(value, options.memberEmail)
}

interface Props {
  value: GuardianValue
  onChange: (value: GuardianValue) => void
  /** The person under 18, named in the declaration. */
  minorName: string
  gymName: string
  /** Ask for the guardian's own address. Omit where the form's email already is theirs. */
  withEmail?: boolean
  /** Ask for the guardian's phone: on joining, not when they sign again later. */
  withPhone?: boolean
  /** The minor's address, which the guardian's must differ from. */
  memberEmail?: string | null
  isDisabled?: boolean
}

/**
 * Who signs for someone under 18: a parent or legal guardian, by name, with a
 * declaration they tick themselves. The box starts unticked, like every other
 * statement on a signing screen, and the Worker refuses the signature without it.
 */
export function GuardianFields({
  value,
  onChange,
  minorName,
  gymName,
  withEmail,
  withPhone,
  memberEmail,
  isDisabled,
}: Props) {
  const { t } = useTranslation()
  const sameEmail = guardianEmailIsOwn(value, memberEmail)
  const badPhone = !!value.phone.trim() && !isValidPhone(value.phone)

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-divider p-3">
      <div>
        <p className="text-sm font-semibold text-foreground">{t('legal.guardianTitle')}</p>
        <p className="text-sm text-foreground">{t('legal.guardianIntro')}</p>
      </div>
      <Input
        label={t('legal.guardianFirstName')}
        value={value.firstName}
        onValueChange={(firstName) => onChange({ ...value, firstName })}
        isRequired
        isDisabled={isDisabled}
      />
      <Input
        label={t('legal.guardianLastName')}
        value={value.lastName}
        onValueChange={(lastName) => onChange({ ...value, lastName })}
        isRequired
        isDisabled={isDisabled}
      />
      {withEmail && (
        <Input
          label={t('legal.guardianEmail')}
          type="email"
          value={value.email}
          onValueChange={(email) => onChange({ ...value, email })}
          isRequired
          isDisabled={isDisabled}
          isInvalid={sameEmail}
          errorMessage={sameEmail ? t('legal.guardianEmailSame') : undefined}
          description={t('legal.guardianEmailHint')}
        />
      )}
      {withPhone && (
        <Input
          label={t('legal.guardianPhone')}
          type="tel"
          placeholder={t('join.phonePlaceholder')}
          value={value.phone}
          onValueChange={(phone) => onChange({ ...value, phone })}
          isRequired
          isDisabled={isDisabled}
          isInvalid={badPhone}
          errorMessage={badPhone ? t('join.phoneInvalidError') : undefined}
          description={t('legal.guardianPhoneHint')}
        />
      )}
      <Checkbox
        isSelected={value.declared}
        onValueChange={(declared) => onChange({ ...value, declared })}
        isDisabled={isDisabled}
        classNames={{ label: 'text-sm text-foreground' }}
      >
        {t('legal.guardianDeclaration', { name: minorName.trim() || '…', gym: gymName })}
      </Checkbox>
    </div>
  )
}
